import { Image, Maximize2, Move, Square, RectangleHorizontal, RectangleVertical, Proportions } from "lucide-react";
import React, { useState, useRef, useCallback, useEffect } from "react";

const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp"];

const PRESET_COLORS = [
    { name: "White", value: "#FFFFFF" },
    { name: "Red", value: "#E54D42" },
    { name: "Pink", value: "#E03E6D" },
    { name: "Purple", value: "#9834B7" },
    { name: "Deep Blue", value: "#4A47A3" },
    { name: "Royal Blue", value: "#3B79C9" },
    { name: "Sky Blue", value: "#4FA0EC" },
    { name: "Cyan", value: "#50B8D6" },
    { name: "Teal", value: "#3D8E83" },
    { name: "Green", value: "#5CA85A" },
    { name: "Light Green", value: "#8FBF5D" },
    { name: "Lime", value: "#D2DB5B" },
    { name: "Yellow", value: "#FCE556" },
    { name: "Amber", value: "#F7BE38" },
    { name: "Orange", value: "#EE9331" },
    { name: "Deep Orange", value: "#EC5E3B" },
    { name: "Brown", value: "#77564C" },
    { name: "Gray", value: "#9E9E9E" },
];

const ASPECT_RATIOS = [
    { name: "Original", value: "original", ratio: null, class: "w-full max-h-[460px]" },
    { name: "Square", value: "1:1", ratio: 1, class: "max-w-[460px] mx-auto" },
    { name: "Landscape", value: "18:9", ratio: 18 / 9, class: "w-full max-h-[410px]" },
    { name: "Portrait", value: "9:18", ratio: 9 / 18, class: "max-h-[460px] mx-auto" },
];

export default function BackgroundRemover() {
    const [originalUrl, setOriginalUrl] = useState(null);
    const [resultUrl, setResultUrl] = useState(null);
    const [fileName, setFileName] = useState("");
    const [status, setStatus] = useState("idle");
    const [errorMsg, setErrorMsg] = useState("");
    const [progress, setProgress] = useState(0);

    // Layout/Comparison Feature States
    const [bgColor, setBgColor] = useState("transparent");
    const [selectedRatio, setSelectedRatio] = useState("original");
    const isResizing = useRef(false);
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    // Foreground Subject Transformation States
    const [fgScale, setFgScale] = useState(1);
    const [fgPos, setFgPos] = useState({ x: 0, y: 0 });
    const [isDraggingFg, setIsDraggingFg] = useState(false);
    const dragStart = useRef({ x: 0, y: 0 });

    const reset = () => {
        setOriginalUrl(null);
        setResultUrl(null);
        setFileName("");
        setStatus("idle");
        setErrorMsg("");
        setProgress(0);
        setBgColor("transparent");
        setSelectedRatio("original");
        setFgScale(1);
        setFgPos({ x: 0, y: 0 });
    };

    const handleFile = useCallback(async (file) => {
        if (!file) return;
        if (!ACCEPTED_TYPES.includes(file.type)) {
            setErrorMsg("Please upload a PNG, JPG, or WEBP image.");
            setStatus("error");
            return;
        }

        setFileName(file.name);
        setOriginalUrl(URL.createObjectURL(file));
        setResultUrl(null);
        setStatus("loading");
        setProgress(0);
        setErrorMsg("");

        try {
            const { removeBackground } = await import("@imgly/background-removal");

            const blob = await removeBackground(file, {
                progress: (key, current, total) => {
                    if (total > 0) {
                        setProgress(Math.round((current / total) * 100));
                    }
                },
            });

            setResultUrl(URL.createObjectURL(blob));
            setStatus("done");
        } catch (err) {
            console.error(err);
            setErrorMsg(
                "Something went wrong while removing the background. Please try a different image."
            );
            setStatus("error");
        }
    }, []);

    const onInputChange = (e) => {
        handleFile(e.target.files?.[0]);
    };

    const onDrop = (e) => {
        e.preventDefault();
        handleFile(e.dataTransfer.files?.[0]);
    };

    // Slider Comparison Move Logic
    const handleMove = (clientX) => {
        if (!containerRef.current || isDraggingFg) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = clientX - rect.left;
        const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    };

    // Subject Layer Pan/Drag Logic
    const handleFgDrag = (clientX, clientY) => {
        if (!isDraggingFg) return;
        const dx = clientX - dragStart.current.x;
        const dy = clientY - dragStart.current.y;
        setFgPos(prev => ({ x: prev.x + dx, y: prev.y + dy }));
        dragStart.current = { x: clientX, y: clientY };
    };

    const onTouchMove = useCallback((e) => {
        if (isDraggingFg) {
            handleFgDrag(e.touches[0].clientX, e.touches[0].clientY);
        } else if (isResizing.current) {
            handleMove(e.touches[0].clientX);
        }
    }, [isDraggingFg]);

    const onMouseMove = useCallback((e) => {
        if (isDraggingFg) {
            handleFgDrag(e.clientX, e.clientY);
        } else if (isResizing.current) {
            handleMove(e.clientX);
        }
    }, [isDraggingFg]);

    const onMouseUp = useCallback(() => {
        isResizing.current = false;
        setIsDraggingFg(false);
    }, []);

    useEffect(() => {
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
        window.addEventListener("touchmove", onTouchMove);
        window.addEventListener("touchend", onMouseUp);
        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
            window.removeEventListener("touchmove", onTouchMove);
            window.removeEventListener("touchend", onMouseUp);
        };
    }, [onMouseMove, onMouseUp, onTouchMove]);

    const onDownload = () => {
        if (!resultUrl) return;

        const img = new window.Image();
        img.src = resultUrl;
        img.onload = () => {
            const canvas = document.createElement("canvas");
            const activeConfig = ASPECT_RATIOS.find((r) => r.value === selectedRatio);

            let targetWidth = img.naturalWidth;
            let targetHeight = img.naturalHeight;

            if (activeConfig && activeConfig.ratio) {
                const imgRatio = img.naturalWidth / img.naturalHeight;
                if (imgRatio > activeConfig.ratio) {
                    targetWidth = img.naturalWidth;
                    targetHeight = img.naturalWidth / activeConfig.ratio;
                } else {
                    targetHeight = img.naturalHeight;
                    targetWidth = img.naturalHeight * activeConfig.ratio;
                }
            }

            canvas.width = targetWidth;
            canvas.height = targetHeight;
            const ctx = canvas.getContext("2d");

            if (bgColor !== "transparent") {
                ctx.fillStyle = bgColor;
                ctx.fillRect(0, 0, canvas.width, canvas.height);
            }


            ctx.save();
            // Apply scale shifts relative to user updates contextually mapping styles
            ctx.translate(targetWidth / 2 + fgPos.x, targetHeight / 2 + fgPos.y);
            ctx.scale(fgScale, fgScale);
            ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2, img.naturalWidth, img.naturalHeight);
            ctx.restore();

            const a = document.createElement("a");
            a.href = canvas.toDataURL("image/png");
            const base = fileName.replace(/\.[^/.]+$/, "");
            const ratioStr = selectedRatio.replace(":", "x");
            a.download = `${base || "image"}-${ratioStr}-${bgColor === "transparent" ? "no-bg" : "custom-bg"}.png`;
            document.body.appendChild(a);
            a.click();
            a.remove();
        };
    };

    const currentRatioConfig = ASPECT_RATIOS.find(r => r.value === selectedRatio);

    return (
        <>
            <style>{`
                @keyframes shimmer {
                    100% { transform: translateX(100%); }
                }
            `}</style>
            <div className="min-h-screen bg-[#0F0F0F] flex text-white relative overflow-hidden flex-col gap-5">

                <div className="absolute top-10 left-4 sm:top-20 sm:left-20 w-40 h-40 sm:w-72 sm:h-72 bg-[#0B3D2E]/40 rounded-full blur-[80px] sm:blur-[120px]" />
                <div className="absolute bottom-10 right-4 sm:bottom-20 sm:right-20 w-40 h-40 sm:w-72 sm:h-72 bg-[#145A43]/25 rounded-full blur-[90px] sm:blur-[140px]" />

                {/* Header */}
                <div className="bg-gradient-to-br from-[#0B3D2E] via-[#145A43] to-[#0B3D2E] border-b border-[#2F8F6F]/20 px-6 py-12 md:py-16 shadow-lg">
                    <div className="max-w-2xl mx-auto flex flex-col justify-center items-center text-center space-y-3">
                        <h1 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight bg-clip-text text-transparent bg-gradient-to-b from-white to-[#D7C7A1]">
                            AI Background Remover
                        </h1>
                        <p className="text-[#D7C7A1] text-xs sm:text-sm max-w-md font-medium leading-relaxed">
                            Remove image backgrounds instantly using AI.
                            <br className="hidden sm:block" />
                            <span className="block sm:inline"> 100% processed inside your browser. No upload. No tracking.</span>
                        </p>
                    </div>
                </div>

                <div className="px-4 sm:px-8 md:px-10 relative mb-10">
                    <div className="relative z-10 w-full max-w-5xl mx-auto rounded-2xl sm:rounded-[32px] bg-white/5 backdrop-blur-2xl border border-[#2F8F6F]/15 p-4 sm:p-8 md:p-10">

                        {status === "idle" && (
                            <label
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={onDrop}
                                className="group flex flex-col items-center justify-center gap-2 sm:gap-3 rounded-2xl sm:rounded-3xl border-2 border-dashed border-[#2F8F6F]/30 bg-[#0B3D2E]/10 h-[220px] sm:h-[300px] px-4 text-center cursor-pointer transition-all duration-300 hover:border-[#2F8F6F]/60 hover:bg-[#0B3D2E]/20 hover:shadow-[0_0_60px_rgba(47,143,111,0.2)]"
                            >
                                <div className="group-hover:scale-110 transition text-[#2F8F6F]">
                                    <Image size={40} className="sm:hidden" />
                                    <Image size={50} className="hidden sm:block" />
                                </div>
                                <h3 className="text-lg sm:text-xl font-bold text-white">Drop your image here</h3>
                                <p className="text-[#D7C7A1]/50 text-sm sm:text-base">Click to browse • PNG • JPG • WEBP</p>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        inputRef.current?.click();
                                    }}
                                    className="mt-2 sm:mt-3 px-5 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base rounded-full bg-gradient-to-r from-[#2F8F6F] to-[#145A43] shadow-lg shadow-[#2F8F6F]/30 hover:scale-105 hover:shadow-[#2F8F6F]/50 transition-all duration-300 text-white font-semibold"
                                >
                                    Choose Image
                                </button>
                                <input
                                    ref={inputRef}
                                    type="file"
                                    accept={ACCEPTED_TYPES.join(",")}
                                    className="hidden"
                                    onChange={onInputChange}
                                />
                            </label>
                        )}

                        {status === "loading" && (
                            <div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                                    <div>
                                        <h3 className="mb-2 sm:mb-3 text-[#2F8F6F] font-semibold text-sm sm:text-base">Original Image</h3>
                                        <img src={originalUrl} alt="Original" className="w-full h-[220px] sm:h-[300px] md:h-[350px] object-contain rounded-2xl sm:rounded-3xl bg-black/30 border border-[#145A43]/40" />
                                    </div>
                                    <div>
                                        <h3 className="mb-2 sm:mb-3 text-[#2F8F6F] font-semibold text-sm sm:text-base">AI Removed Background</h3>
                                        <div
                                            className="relative w-full h-[220px] sm:h-[300px] md:h-[350px] rounded-2xl sm:rounded-3xl border border-[#145A43]/40 overflow-hidden flex items-center justify-center"
                                            style={{
                                                backgroundImage: `linear-gradient(45deg,#ffffff10 25%,transparent 25%), linear-gradient(-45deg,#ffffff10 25%,transparent 25%), linear-gradient(45deg,transparent 75%,#ffffff10 75%), linear-gradient(-45deg,transparent 75%,#ffffff10 75%)`,
                                                backgroundSize: "30px 30px",
                                                backgroundPosition: "0 0,0 15px,15px -15px,-15px 0px",
                                            }}
                                        >
                                            <img src={originalUrl} alt="" className="absolute inset-0 w-full h-full object-contain opacity-20 blur-sm" />
                                            <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.6s_infinite] bg-gradient-to-r from-transparent via-[#2F8F6F]/10 to-transparent" />
                                            <div className="absolute inset-4 sm:inset-6 rounded-xl sm:rounded-2xl bg-[#0B3D2E]/20 animate-pulse" />
                                        </div>
                                    </div>
                                </div>
                                <div className="w-full max-w-md mx-auto mt-6 sm:mt-8">
                                    <div className="h-2.5 sm:h-3 bg-white/10 rounded-full overflow-hidden">
                                        <div className="h-full bg-gradient-to-r from-[#2F8F6F] to-[#D7C7A1] transition-all duration-300" style={{ width: `${progress}%` }} />
                                    </div>
                                    <p className="text-center mt-3 sm:mt-4 text-sm sm:text-base text-[#D7C7A1]/70">Removing background...{progress > 0 && ` ${progress}%`}</p>
                                </div>
                            </div>
                        )}

                        {status === "error" && (
                            <div className="text-center py-6 sm:py-10 px-2">
                                <p className="text-red-400 mb-4 sm:mb-5 text-sm sm:text-base">{errorMsg}</p>
                                <button onClick={reset} className="px-5 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base rounded-full bg-red-500/20 border border-red-500/30 hover:bg-red-500/30 transition">Try Again</button>
                            </div>
                        )}

                        {status === "done" && (
                            <div className="flex flex-col gap-6">
                                {/* Interactive Comparison Slider */}
                                <div className="w-full flex flex-col items-center">
                                    <h3 className="flex flex-row gap-2 items-center justify-center mb-3 text-[#2F8F6F] font-semibold text-sm sm:text-base text-center w-full">
                                        <Move /> Drag on right side to move subject
                                    </h3>

                                    <div className="w-full max-w-full overflow-hidden flex items-center justify-center p-1">
                                        <div
                                            ref={containerRef}
                                            className={`relative h-[300px] sm:h-[400px] rounded-2xl sm:rounded-3xl border border-[#145A43]/40 overflow-hidden select-none touch-none transition-all duration-300 ${currentRatioConfig?.class || ''}`}
                                            style={{ aspectRatio: currentRatioConfig?.ratio || 'auto' }}
                                        >
                                            {/* Layer A (Background): The New Edit (Removed BG + Selected Custom Color/Canvas BG) */}
                                            <div
                                                className="absolute inset-0 w-full h-full flex items-center justify-center cursor-move"
                                                onMouseDown={(e) => {
                                                    setIsDraggingFg(true);
                                                    dragStart.current = { x: e.clientX, y: e.clientY };
                                                }}
                                                onTouchStart={(e) => {
                                                    setIsDraggingFg(true);
                                                    dragStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
                                                }}
                                                style={{
                                                    backgroundColor: bgColor === "transparent" ? "transparent" : bgColor,
                                                    backgroundImage: bgColor === "transparent" ? `linear-gradient(45deg,#ffffff10 25%,transparent 25%), linear-gradient(-45deg,#ffffff10 25%,transparent 25%), linear-gradient(45deg,transparent 75%,#ffffff10 75%), linear-gradient(-45deg,transparent 75%,#ffffff10 75%)` : "none",
                                                    backgroundSize: "30px 30px",
                                                }}
                                            >
                                                <img
                                                    src={resultUrl}
                                                    alt="Resulted background removed"
                                                    className="max-h-full max-w-full object-contain pointer-events-none transition-transform duration-75 ease-out"
                                                    style={{
                                                        transform: `translate(${fgPos.x}px, ${fgPos.y}px) scale(${fgScale})`
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Subject Adjustment Transformations Controls */}
                                <div className="p-4 rounded-xl bg-white/5 border border-[#2F8F6F]/10 space-y-3">
                                    <div className="flex flex-row items-center justify-between">
                                        <span className="text-xs md:text-sm font-semibold text-[#D7C7A1] flex items-center gap-2">
                                            <Maximize2 size={16} className="text-[#2F8F6F]" /> Resize & Position Subject
                                        </span>
                                        <button
                                            onClick={() => { setFgScale(1); setFgPos({ x: 0, y: 0 }); }}
                                            className="text-[10px] md:text-xs text-gray-400 bg-white/5 border border-white/10 hover:border-white/20 px-2.5 py-1 rounded-md transition"
                                        >
                                            Reset Layout
                                        </button>
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-4 items-center">
                                        <div className="w-full flex items-center gap-3">
                                            <span className="text-xs text-gray-400 min-w-10">Scale:</span>
                                            <input
                                                type="range"
                                                min="0.5"
                                                max="2.5"
                                                step="0.05"
                                                value={fgScale}
                                                onChange={(e) => setFgScale(parseFloat(e.target.value))}
                                                className="w-full accent-[#2F8F6F] bg-white/10 h-1.5 rounded-lg appearance-none cursor-pointer"
                                            />
                                            <span className="text-xs font-mono text-gray-300 w-10 text-right">{Math.round(fgScale * 100)}%</span>
                                        </div>
                                        <div className="hidden sm:flex items-center text-xs text-gray-500 gap-1 select-none">
                                            <Move size={14} />
                                            <span>Pannable via viewport dragging</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Aspect Ratio Controls */}
                                <div className="p-4 rounded-xl bg-white/5 border border-[#2F8F6F]/10 space-y-3">
                                    <span className="text-sm font-semibold text-[#D7C7A1]">Select Image Ratio:</span>
                                    <div className="flex flex-wrap gap-2.5">
                                        {ASPECT_RATIOS.map((ratio) => (
                                            <button
                                                key={ratio.name}
                                                onClick={() => setSelectedRatio(ratio.value)}
                                                className={`flex flex-row gap-2 items-center px-4 py-2 rounded-lg text-xs font-medium transition-all ${selectedRatio === ratio.value
                                                    ? "bg-[#2F8F6F] text-white border border-[#2F8F6F]"
                                                    : "bg-[#0F0F0F]/60 text-gray-300 border border-white/10 hover:border-white/30"
                                                    }`}
                                            >
                                                {ratio.value === '1:1' ?
                                                    <Square size={18} /> :
                                                    ratio.value === '18:9' ?
                                                        <RectangleHorizontal size={18} /> :
                                                        ratio.value === '9:18' ?
                                                            <RectangleVertical size={18} /> :
                                                            <Proportions size={18} />}
                                                {ratio.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Background Swatch Grid Controls */}
                                <div className="p-5 rounded-2xl bg-white/5 border border-[#2F8F6F]/10 space-y-4">
                                    <span className="text-sm font-semibold text-[#D7C7A1] block">Background Color</span>

                                    <div className="flex flex-row flex-wrap gap-3">
                                        {/* 1. Transparent Button Swatch */}
                                        <button
                                            type="button"
                                            onClick={() => setBgColor("transparent")}
                                            className={`w-[60px] h-[60px] md:w-16 md:h-16 rounded-2xl flex items-center justify-center bg-transparent border-2 transition-all relative overflow-hidden group ${bgColor === "transparent"
                                                ? "border-blue-500 shadow-[0_0_12px_rgba(59,130,246,0.5)] scale-95"
                                                : "border-white/20 hover:border-white/40"
                                                }`}
                                        >
                                            <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                                            <span className="text-xl font-light text-white/70 select-none">🛇</span>
                                        </button>

                                        {/* 2. Color Picker Spectrum Swatch */}
                                        <label
                                            className={`w-[60px] h-[60px] md:w-16 md:h-16 rounded-2xl cursor-pointer transition-all relative overflow-hidden group ${bgColor !== "transparent" && !PRESET_COLORS.some(c => c.value.toLowerCase() === bgColor.toLowerCase())
                                                ? "border-blue-500 shadow-[0_0_12px_rgba(59,130,246,0.5)] scale-95"
                                                : "border-white/20 hover:border-white/40"
                                                }`}
                                            style={{
                                                background: "conic-gradient(from 180deg at 50% 50%, #FF0000 0deg, #FF00FF 60deg, #0000FF 120deg, #00FFFF 180deg, #00FF00 240deg, #FFFF00 300deg, #FF0000 360deg)"
                                            }}
                                        >
                                            <input
                                                type="color"
                                                value={bgColor === "transparent" ? "#ffffff" : bgColor}
                                                onChange={(e) => setBgColor(e.target.value)}
                                                className="sr-only"
                                            />
                                        </label>

                                        {/* Preset Color Swatches */}
                                        {PRESET_COLORS.map((color) => {
                                            const isSelected = bgColor.toLowerCase() === color.value.toLowerCase();
                                            return (
                                                <button
                                                    key={color.name}
                                                    type="button"
                                                    onClick={() => setBgColor(color.value)}
                                                    className={`w-[60px] h-[60px] md:w-16 md:h-16 rounded-2xl border-2 transition-all ${isSelected
                                                        ? "border-blue-500 shadow-[0_0_12px_rgba(59,130,246,0.5)] scale-95"
                                                        : "border-white/10 hover:border-white/30"
                                                        }`}
                                                    style={{ backgroundColor: color.value }}
                                                    title={color.name}
                                                />
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex flex-col sm:flex-row sm:flex-wrap justify-center gap-3 sm:gap-4 mt-2">
                                    <button
                                        onClick={onDownload}
                                        className="w-full sm:w-auto px-8 py-3 text-sm sm:text-base rounded-full bg-[#2F8F6F] font-bold text-[#0F0F0F] hover:scale-105 transition"
                                    >
                                        Download PNG
                                    </button>
                                    <button
                                        onClick={reset}
                                        className="w-full sm:w-auto px-8 py-3 text-sm sm:text-base rounded-full bg-[#0B3D2E]/30 backdrop-blur border border-[#2F8F6F]/20 text-[#D7C7A1] hover:bg-[#0B3D2E]/50 hover:border-[#2F8F6F]/40 transition"
                                    >
                                        Try Another Image
                                    </button>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
                <div className="text-gray-400 flex w-full justify-center text-xs md:text-md text-end bg-[#0F0F0F] pb-10">
                    <p>Copyright © 2025 Rizki Makruf. All rights reserved.</p>
                </div>
            </div >
        </>
    );
}